import { z } from "zod"

/**
 * Standard header name for idempotent mutation requests (ADR-010, ADR-015).
 */
export const IDEMPOTENCY_KEY_HEADER = "Idempotency-Key"

/**
 * Idempotency key format for write operations (ADR-015).
 * The value is typically constructed as `${entity_id}:${local_seq}`.
 * The server deduplicates and replays responses within a 24-hour window.
 */
export const idempotencyKeySchema = z.string().min(1)

export type IdempotencyKey = z.infer<typeof idempotencyKeySchema>

/**
 * Monotonically increasing integer revision assigned by the server (ADR-015).
 * The mobile client stores the last seen revision as `base_revision` for patch comparison
 * and optimistic concurrency control.
 */
export const revisionSchema = z.number().int().nonnegative()

export type Revision = z.infer<typeof revisionSchema>

/**
 * Entities supported by the sync change log (s4-trips-api-brief §2.5).
 */
export const SYNC_ENTITIES = [
  "trips",
  "vehicles",
  "odometer_readings",
  "trip_tracks",
  "documents",
  "extractions",
  "expenses",
  "logbook_periods",
  "customers",
  "invoices",
  "payments",
  "payment_receipts",
  "organisation_branding",
  "seller_profiles",
] as const
export const syncEntitySchema = z.enum(SYNC_ENTITIES)

export type SyncEntity = z.infer<typeof syncEntitySchema>

/**
 * Entities GET /api/v1/changes returns when the `entities` query parameter is omitted. Frozen at
 * the original four so /v1 stays additive: the receipts entities must be requested explicitly.
 */
export const DEFAULT_CHANGES_ENTITIES = [
  "trips",
  "vehicles",
  "odometer_readings",
  "trip_tracks",
] as const satisfies readonly SyncEntity[]
